import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { createHttpClient } from "@shared/api/httpClient";
import { formatLocalDateTime } from "@shared/utils/dateTime";
import { friendlyLabel } from "@shared/utils/friendlyLabels";
import { displayNameForIdentity } from "@shared/utils/privacy";
import { statusPillClassName } from "@/utils/statusPill";
import {
  useAiAssistant,
  useAiAssistantPageScope,
} from "@/components/AiAssistantDrawer";

type ApiRecord = Record<string, unknown>;

type LoadState = {
  loading: boolean;
  packageRecord: ApiRecord;
  controls: ApiRecord[];
  documents: ApiRecord[];
  error?: string;
};

type ControlFilter = "ALL" | "PASS" | "PARTIAL" | "FAIL" | "NOT_APPLICABLE";

const CONTROL_FILTERS: Array<{ value: ControlFilter; label: string }> = [
  { value: "ALL", label: "All controls" },
  { value: "PASS", label: "PASS" },
  { value: "PARTIAL", label: "PARTIAL" },
  { value: "FAIL", label: "FAIL" },
  { value: "NOT_APPLICABLE", label: "NOT_APPLICABLE" },
];

function asRecord(value: unknown): ApiRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as ApiRecord)
    : {};
}

function asArray(value: unknown): ApiRecord[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is ApiRecord =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item),
      )
    : [];
}

function recordsFromResponse(response: unknown, keys: string[]): ApiRecord[] {
  const root = asRecord(response);
  for (const key of keys) {
    const records = asArray(root[key]);
    if (records.length > 0) return records;
  }
  return [];
}

function recordFromResponse(response: unknown, keys: string[]): ApiRecord {
  const root = asRecord(response);
  for (const key of keys) {
    const record = asRecord(root[key]);
    if (Object.keys(record).length > 0) return record;
  }
  return root;
}

function text(record: ApiRecord | undefined, ...keys: string[]): string {
  const item = record ?? {};
  for (const key of keys) {
    const value = item[key];
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      const joined = value
        .map((entry) => String(entry ?? "").trim())
        .filter(Boolean)
        .join(", ");
      if (joined) return joined;
      continue;
    }
    const stringValue = String(value).trim();
    if (stringValue) return stringValue;
  }
  return "";
}

function valueOrDash(value: unknown): string {
  if (value === undefined || value === null || String(value).trim() === "")
    return "—";
  if (Array.isArray(value)) return value.join(", ") || "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function numberValue(record: ApiRecord, ...keys: string[]): number {
  for (const key of keys) {
    const raw = record[key];
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    if (typeof raw === "string" && raw.trim()) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return 0;
}

function objectValue(record: ApiRecord | undefined, key: string): ApiRecord {
  const value = (record ?? {})[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as ApiRecord)
    : {};
}

function scopeNumber(
  record: ApiRecord,
  nestedKey: string,
  ...flatKeys: string[]
): number {
  const nested = objectValue(record, "scopeSummary");
  for (const key of flatKeys) {
    const value = numberValue(record, key);
    if (value) return value;
  }
  const nestedValue = nested[nestedKey];
  if (typeof nestedValue === "number" && Number.isFinite(nestedValue))
    return nestedValue;
  if (typeof nestedValue === "string" && nestedValue.trim()) {
    const parsed = Number(nestedValue);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function scopeCounts(record: ApiRecord) {
  const nested = objectValue(record, "scopeSummary");
  return {
    total: scopeNumber(record, "totalAssets", "scopeTotalAssets"),
    inScope: scopeNumber(record, "inScopeAssets", "scopeInScopeAssets"),
    outOfScope: scopeNumber(
      record,
      "outOfScopeAssets",
      "scopeOutOfScopeAssets",
    ),
    unknown: scopeNumber(
      record,
      "unknownScopeAssets",
      "scopeUnknownScopeAssets",
    ),
    cui: scopeNumber(record, "cuiAssets", "scopeCuiAssets"),
    securityProtection: scopeNumber(
      record,
      "securityProtectionAssets",
      "scopeSecurityProtectionAssets",
    ),
    clientEvidencePackage: scopeNumber(
      record,
      "clientEvidencePackageAssets",
      "scopeClientEvidencePackageAssets",
    ),
    evidenceEligible: scopeNumber(
      record,
      "evidenceEligibleAssets",
      "scopeEvidenceEligibleAssets",
    ),
    missingTags: scopeNumber(
      record,
      "missingRequiredTagFindings",
      "scopeMissingRequiredTagFindings",
    ),
    invalidTags: scopeNumber(
      record,
      "invalidTagFindings",
      "scopeInvalidTagFindings",
    ),
    validationStatus:
      text(record, "scopeValidationStatus") ||
      String(nested.validationStatus || ""),
  };
}

function boolValue(record: ApiRecord, key: string): boolean {
  const raw = record[key];
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "string") return raw.trim().toLowerCase() === "true";
  return false;
}

function scopeReviewCounts(record: ApiRecord) {
  const nested = objectValue(record, "scopeSummary");
  const awsManaged = scopeNumber(
    record,
    "unknownScopeLikelyAwsManaged",
    "scopeUnknownScopeLikelyAwsManaged",
  );
  const controlTower = scopeNumber(
    record,
    "unknownScopeLikelyControlTower",
    "scopeUnknownScopeLikelyControlTower",
  );
  const cdkBootstrap = scopeNumber(
    record,
    "unknownScopeLikelyCdkBootstrap",
    "scopeUnknownScopeLikelyCdkBootstrap",
  );
  const platformSupport = scopeNumber(
    record,
    "unknownScopeLikelyPlatformSupport",
    "scopeUnknownScopeLikelyPlatformSupport",
  );
  const clientCreated = scopeNumber(
    record,
    "unknownScopeClientCreatedUnknown",
    "scopeUnknownScopeClientCreatedUnknown",
  );
  const requiresReview = scopeNumber(
    record,
    "unknownScopeRequiresClientReview",
    "scopeUnknownScopeRequiresClientReview",
  );
  const unknown = scopeNumber(
    record,
    "unknownScopeAssets",
    "scopeUnknownScopeAssets",
  );
  const classificationPresent =
    boolValue(record, "scopeUnknownScopeClassificationPresent") ||
    String(nested.unknownScopeClassificationPresent || "").toLowerCase() ===
      "true";
  const likelyManagedOrPlatform =
    awsManaged + controlTower + cdkBootstrap + platformSupport;
  return {
    classificationPresent,
    unknown,
    awsManaged,
    controlTower,
    cdkBootstrap,
    platformSupport,
    clientCreated,
    requiresReview,
    likelyManagedOrPlatform,
  };
}

function hasScopeReviewContext(record: ApiRecord): boolean {
  const counts = scopeReviewCounts(record);
  return Boolean(
    counts.classificationPresent ||
    counts.awsManaged ||
    counts.controlTower ||
    counts.cdkBootstrap ||
    counts.platformSupport ||
    counts.clientCreated ||
    counts.requiresReview,
  );
}

function hasScopeMetadata(record: ApiRecord): boolean {
  const counts = scopeCounts(record);
  return Boolean(
    text(record, "resourceScopeMetadataPresent").toLowerCase() === "true" ||
    text(record, "scopeValidationStatus") ||
    Object.keys(objectValue(record, "scopeSummary")).length > 0 ||
    counts.total ||
    counts.inScope ||
    counts.outOfScope ||
    counts.unknown ||
    counts.missingTags ||
    counts.invalidTags,
  );
}

function scopeSummary(record: ApiRecord): string {
  if (!hasScopeMetadata(record)) return "—";
  const counts = scopeCounts(record);
  const parts = [
    `In ${counts.inScope}`,
    `Out ${counts.outOfScope}`,
    `Unknown ${counts.unknown}`,
  ];
  if (counts.total) parts.push(`Total ${counts.total}`);
  return parts.join(" / ");
}

function scopeExceptionSummary(record: ApiRecord): string {
  if (!hasScopeMetadata(record)) return "—";
  const counts = scopeCounts(record);
  const parts = [];
  if (counts.unknown) parts.push(`${counts.unknown} unknown scope`);
  if (counts.missingTags)
    parts.push(
      `${counts.missingTags} missing tag finding${counts.missingTags === 1 ? "" : "s"}`,
    );
  if (counts.invalidTags)
    parts.push(
      `${counts.invalidTags} invalid tag finding${counts.invalidTags === 1 ? "" : "s"}`,
    );
  if (counts.validationStatus)
    parts.push(`Validation ${counts.validationStatus}`);
  return parts.length ? parts.join(" / ") : "No scope exceptions indexed";
}

function scopeAssetSummary(record: ApiRecord): string {
  if (!hasScopeMetadata(record)) return "—";
  const counts = scopeCounts(record);
  const parts = [];
  if (counts.cui) parts.push(`${counts.cui} CUI`);
  if (counts.securityProtection)
    parts.push(`${counts.securityProtection} security protection`);
  if (counts.clientEvidencePackage)
    parts.push(`${counts.clientEvidencePackage} evidence package`);
  if (counts.evidenceEligible)
    parts.push(`${counts.evidenceEligible} evidence eligible`);
  return parts.length ? parts.join(" / ") : "—";
}

function packageId(record: ApiRecord): string {
  return text(record, "logicalPackageId", "packageId", "id", "packageName");
}

function inferredPackageType(packageIdentifier: string): string {
  const lowered = packageIdentifier.toLowerCase();
  if (lowered.includes("final-auditor")) return "FINAL_AUDITOR_PACKAGE";
  if (lowered.includes("technical-evidence") || lowered.includes("evidence-package"))
    return "TECHNICAL_EVIDENCE_PACKAGE";
  return "";
}

function packageName(record: ApiRecord): string {
  return (
    text(record, "packageName", "finalAuditorPackageName", "packageId") ||
    "Evidence package"
  );
}

function compactPackageIdentifier(identifier: string, maxLength = 46): string {
  const value = String(identifier || "").trim();
  if (!value) return "—";
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(10, maxLength - 14))}…${value.slice(-10)}`;
}

function packageGeneratedLabel(record: ApiRecord): string {
  const candidate =
    text(record, "generatedUtc", "createdAt", "indexedAt") || packageId(record);
  const match = candidate.match(/20\d{2}-\d{2}-\d{2}T\d{2}[-:]\d{2}[-:]\d{2}Z/);
  if (!match) return "";
  const isoTimestamp = match[0].replace(
    /T(\d{2})-(\d{2})-(\d{2})Z$/,
    "T$1:$2:$3Z",
  );
  return formatLocalDateTime(isoTimestamp);
}

function packageDisplayTitle(record: ApiRecord): string {
  const id = packageId(record);
  const explicitName = text(record, "finalAuditorPackageName", "packageName");
  if (explicitName && explicitName !== id) return explicitName;
  const type = friendlyLabel(text(record, "packageType")) || "Evidence package";
  const generated = packageGeneratedLabel(record);
  return generated && generated !== "—" ? `${type} · ${generated}` : type;
}

function packageOwnerFields(record: ApiRecord): Array<[string, unknown]> {
  const rows: Array<[string, unknown]> = [
    ["Company", text(record, "clientName", "companyName")],
    ["Client ID", text(record, "clientId")],
    [
      "Account ID",
      text(
        record,
        "accountId",
        "customerAccountId",
        "workloadAccountId",
        "awsAccountId",
      ),
    ],
    ["Region", text(record, "region", "awsRegion")],
    [
      "Owner",
      text(
        record,
        "owner",
        "ownerName",
        "packageOwner",
        "customerOwner",
        "engagementOwner",
      ),
    ],
    [
      "Contact",
      displayNameForIdentity(
        {
          name: text(record, "ownerName", "customerOwner", "engagementOwner"),
          email: text(
            record,
            "ownerEmail",
            "customerContactEmail",
            "invitationEmail",
            "primaryContactEmail",
          ),
        },
        "Package contact",
      ),
    ],
  ];
  return rows.filter(([, detailValue]) => valueOrDash(detailValue) !== "—");
}

function packageStatus(record: ApiRecord): string {
  return (
    text(
      record,
      "status",
      "displayStatus",
      "packageLifecycleStatus",
      "packageStage",
    ) || "UNKNOWN"
  );
}

function controlId(control: ApiRecord): string {
  return text(control, "controlId", "id");
}

function controlStatus(control: ApiRecord): string {
  return (
    text(control, "status", "controlStatus", "sourceControlStatus") || "UNKNOWN"
  );
}

function readiness(record: ApiRecord) {
  const pass = numberValue(record, "passCount", "PASS");
  const partial = numberValue(record, "partialCount", "PARTIAL");
  const fail = numberValue(record, "failCount", "FAIL");
  const notApplicable = numberValue(
    record,
    "notApplicableCount",
    "NOT_APPLICABLE",
  );
  const total =
    numberValue(record, "totalControls", "TOTAL") ||
    pass + partial + fail + notApplicable;
  return { pass, partial, fail, notApplicable, total };
}

function readinessSummary(record: ApiRecord): string {
  const counts = readiness(record);
  if (
    !counts.total &&
    !counts.pass &&
    !counts.partial &&
    !counts.fail &&
    !counts.notApplicable
  )
    return "—";
  return `PASS ${counts.pass} / PARTIAL ${counts.partial} / FAIL ${counts.fail} / N/A ${counts.notApplicable} / TOTAL ${counts.total}`;
}

function readinessPercent(record: ApiRecord): number | null {
  const counts = readiness(record);
  if (!counts.total) return null;
  return (
    Math.round(((counts.pass + counts.notApplicable) / counts.total) * 1000) /
    10
  );
}

function ReadinessBadges({ record }: { record: ApiRecord }) {
  const counts = readiness(record);
  if (
    !counts.total &&
    !counts.pass &&
    !counts.partial &&
    !counts.fail &&
    !counts.notApplicable
  )
    return <span>—</span>;
  const resolvedPercent = readinessPercent(record);
  return (
    <div className="readiness-chip-grid">
      <span className="readiness-chip readiness-chip-pass">
        <strong>{counts.pass}</strong> PASS
      </span>
      <span className="readiness-chip readiness-chip-partial">
        <strong>{counts.partial}</strong> PARTIAL
      </span>
      <span className="readiness-chip readiness-chip-fail">
        <strong>{counts.fail}</strong> FAIL
      </span>
      <span className="readiness-chip readiness-chip-na">
        <strong>{counts.notApplicable}</strong> N/A
      </span>
      <span className="readiness-chip readiness-chip-total">
        <strong>{counts.total}</strong> TOTAL
      </span>
      {resolvedPercent !== null && (
        <span className="readiness-chip readiness-chip-score">
          <strong>{resolvedPercent}%</strong> resolved
        </span>
      )}
    </div>
  );
}

function adminReasonAction(control: ApiRecord): string {
  const reason = text(
    control,
    "adminReason",
    "displayReason",
    "clientReason",
    "reason",
    "statusReason",
  );
  const action = text(
    control,
    "adminAction",
    "recommendedAction",
    "clientAction",
    "actionRequiredReason",
  );
  if (reason && action && reason !== action)
    return `Reason: ${reason} Action: ${action}`;
  return reason || action;
}

function clientReasonAction(control: ApiRecord): string {
  const reason = text(control, "clientReason", "displayReason");
  const action = text(control, "clientAction", "recommendedAction");
  if (reason && action && reason !== action)
    return `Reason: ${reason} Action: ${action}`;
  return reason || action;
}

function detailRow(label: string, detailValue: unknown) {
  return (
    <div className="detail-row" key={label}>
      <dt>{label}</dt>
      <dd>{valueOrDash(detailValue)}</dd>
    </div>
  );
}

function ControlDetail({ control }: { control: ApiRecord }) {
  const adminReason = text(
    control,
    "adminReason",
    "displayReason",
    "clientReason",
    "reason",
    "statusReason",
  );
  const adminAction = text(
    control,
    "adminAction",
    "recommendedAction",
    "clientAction",
    "actionRequiredReason",
  );
  const adminGap = text(
    control,
    "adminEvidenceGap",
    "evidenceGap",
    "clientEvidenceGap",
  );
  const clientReason = text(control, "clientReason", "displayReason");
  const clientAction = text(control, "clientAction", "recommendedAction");
  const clientGap = text(control, "clientEvidenceGap", "evidenceGap");

  return (
    <details className="control-detail-panel">
      <summary>Reason / action detail</summary>
      <div className="control-detail-grid">
        <section>
          <h4>Internal SURELINC admin guidance</h4>
          <dl>
            {detailRow("Admin reason", adminReason)}
            {detailRow("Admin action", adminAction)}
            {detailRow("Admin evidence gap", adminGap)}
            {detailRow("Category", text(control, "actionCategory"))}
            {detailRow("Owner", text(control, "ownerHint"))}
            {detailRow(
              "Confidence",
              text(control, "reasonActionConfidence", "confidence"),
            )}
            {detailRow("Source", text(control, "reasonActionSource"))}
          </dl>
        </section>
        <section>
          <h4>Client-facing preview</h4>
          <dl>
            {detailRow("Client reason", clientReason)}
            {detailRow("Client action", clientAction)}
            {detailRow("Client evidence gap", clientGap)}
          </dl>
        </section>
      </div>
    </details>
  );
}

export default function PackageDetailPage() {
  const api = useMemo(() => createHttpClient({ portal: "admin" }), []);
  const { clientId = "", accountId = "", packageId: routePackageId = "" } = useParams();
  const decodedClientId = decodeURIComponent(clientId);
  const decodedAccountId = decodeURIComponent(accountId);
  const decodedPackageId = decodeURIComponent(routePackageId);
  const [state, setState] = useState<LoadState>({
    loading: true,
    packageRecord: {},
    controls: [],
    documents: [],
  });
  const [controlFilter, setControlFilter] = useState<ControlFilter>("ALL");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");
  const { openDrawer } = useAiAssistant();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!decodedClientId || !decodedAccountId || !decodedPackageId) {
        setState({
          loading: false,
          packageRecord: {},
          controls: [],
          documents: [],
          error: "Client ID, account ID, and package ID are required.",
        });
        return;
      }

      setState((current) => ({ ...current, loading: true, error: undefined }));
      try {
        const [packageResponse, controlsResponse, documentsResponse] =
          await Promise.all([
            api.get<unknown>(
              `/admin/clients/${encodeURIComponent(decodedClientId)}/packages/${encodeURIComponent(decodedPackageId)}`,
              { query: { accountId: decodedAccountId } },
            ),
            api.get<unknown>(
              `/admin/clients/${encodeURIComponent(decodedClientId)}/packages/${encodeURIComponent(decodedPackageId)}/controls`,
              {
                query: { accountId: decodedAccountId, limit: 1000 },
              },
            ),
            api
              .get<unknown>(
                `/admin/clients/${encodeURIComponent(decodedClientId)}/packages/${encodeURIComponent(decodedPackageId)}/documents`,
                {
                  query: { accountId: decodedAccountId, limit: 1000 },
                },
              )
              .catch(() => ({ documents: [] })),
          ]);

        if (!cancelled) {
          setState({
            loading: false,
            packageRecord: recordFromResponse(packageResponse, ["package"]),
            controls: recordsFromResponse(controlsResponse, [
              "controls",
              "items",
            ]),
            documents: recordsFromResponse(documentsResponse, [
              "documents",
              "items",
            ]),
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            loading: false,
            packageRecord: {},
            controls: [],
            documents: [],
            error:
              error instanceof Error
                ? error.message
                : "Unable to load package detail.",
          });
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [api, decodedAccountId, decodedClientId, decodedPackageId]);

  const categories = useMemo(() => {
    const values = new Set<string>();
    for (const control of state.controls) {
      const category = text(control, "actionCategory");
      if (category) values.add(category);
    }
    return Array.from(values).sort();
  }, [state.controls]);

  const filteredControls = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return state.controls.filter((control) => {
      if (
        controlFilter !== "ALL" &&
        controlStatus(control).toUpperCase() !== controlFilter
      )
        return false;
      if (categoryFilter && text(control, "actionCategory") !== categoryFilter)
        return false;
      if (!needle) return true;
      const haystack = [
        controlId(control),
        text(control, "title"),
        controlStatus(control),
        text(control, "actionCategory"),
        text(control, "ownerHint"),
        adminReasonAction(control),
        clientReasonAction(control),
        text(control, "adminEvidenceGap", "clientEvidenceGap", "evidenceGap"),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [state.controls, controlFilter, categoryFilter, search]);

  const packageRecord = state.packageRecord;
  const generatedAt = text(packageRecord, "reasonActionGeneratedAt");
  const tokenSummary = [
    text(packageRecord, "reasonActionInputTokens")
      ? `${text(packageRecord, "reasonActionInputTokens")} input tokens`
      : "",
    text(packageRecord, "reasonActionOutputTokens")
      ? `${text(packageRecord, "reasonActionOutputTokens")} output tokens`
      : "",
  ]
    .filter(Boolean)
    .join(" / ");
  const aiPackageScope = useMemo(
    () => ({
      clientId: text(packageRecord, "clientId") || decodedClientId,
      accountId: text(packageRecord, "accountId") || decodedAccountId,
      clientName: text(packageRecord, "clientName") || decodedClientId,
      packageId: decodedPackageId || packageId(packageRecord),
      packageName: packageDisplayTitle(packageRecord) || decodedPackageId,
      packageType:
        text(packageRecord, "packageType") ||
        inferredPackageType(decodedPackageId || packageId(packageRecord)),
      pageType: "PACKAGE_DETAIL",
      sourceLabel: "Package detail page",
    }),
    [decodedAccountId, decodedClientId, decodedPackageId, packageRecord],
  );

  useAiAssistantPageScope(aiPackageScope);

  function askAboutPackage(
    prompt = "What should I work on next for this package?",
  ) {
    openDrawer(aiPackageScope, prompt, true);
  }

  function askAboutControl(control: ApiRecord) {
    const selectedControlId = controlId(control);
    if (!selectedControlId) return;
    openDrawer(
      {
        ...aiPackageScope,
        controlId: selectedControlId,
        controlTitle: text(control, "title"),
        pageType: "CONTROL_DETAIL",
      },
      `Explain ${selectedControlId}, what evidence supports its current status, and what still needs to be done.`,
      true,
    );
  }

  return (
    <div className="page-stack admin-package-detail-page">
      <div className="page-heading">
        <Link className="secondary-button" to="/evidence-packages">
          ← Evidence Packages
        </Link>
        <h2>Package Control Detail</h2>
        <p>
          Internal SURELINC control-level reason/action guidance for package
          review and remediation planning.
        </p>
        <div className="page-heading-actions">
          <button
            className="primary-button"
            type="button"
            onClick={() => askAboutPackage()}
          >
            ✦ Ask AI about this package
          </button>
        </div>
      </div>

      {state.loading && (
        <article className="card">
          <p>Loading package detail...</p>
        </article>
      )}
      {state.error && (
        <article className="card">
          <p className="error-text">{state.error}</p>
        </article>
      )}

      {!state.loading && !state.error && (
        <>
          <article className="card detail-panel">
            <div className="detail-panel-header">
              <div>
                <h3>{packageDisplayTitle(packageRecord)}</h3>
                <p className="muted-text">
                  {text(packageRecord, "clientName", "clientId") ||
                    decodedClientId}{" "}
                  ·{" "}
                  <span title={decodedPackageId}>
                    {compactPackageIdentifier(decodedPackageId)}
                  </span>
                </p>
              </div>
              <span
                className={statusPillClassName(packageStatus(packageRecord))}
              >
                {friendlyLabel(packageStatus(packageRecord))}
              </span>
            </div>

            <div className="detail-grid">
              <section>
                <h4>Package</h4>
                <dl>
                  {detailRow(
                    "Client ID",
                    text(packageRecord, "clientId") || decodedClientId,
                  )}
                  {detailRow(
                    "Package ID",
                    compactPackageIdentifier(
                      packageId(packageRecord) || decodedPackageId,
                      58,
                    ),
                  )}
                  {detailRow("Job ID", text(packageRecord, "jobId"))}
                  <div className="detail-row">
                    <dt>Type</dt>
                    <dd>
                      <span
                        className={statusPillClassName(
                          text(packageRecord, "packageType"),
                        )}
                      >
                        {friendlyLabel(text(packageRecord, "packageType"))}
                      </span>
                    </dd>
                  </div>
                  <div className="detail-row">
                    <dt>Stage</dt>
                    <dd>
                      <span
                        className={statusPillClassName(
                          text(packageRecord, "packageStage"),
                        )}
                      >
                        {friendlyLabel(text(packageRecord, "packageStage"))}
                      </span>
                    </dd>
                  </div>
                  {detailRow(
                    "Updated",
                    formatLocalDateTime(
                      text(
                        packageRecord,
                        "updatedAt",
                        "indexedAt",
                        "createdAt",
                      ),
                    ),
                  )}
                </dl>
              </section>
              {packageOwnerFields(packageRecord).length > 0 && (
                <section>
                  <h4>Client / owner</h4>
                  <dl>
                    {packageOwnerFields(packageRecord).map(
                      ([label, detailValue]) => detailRow(label, detailValue),
                    )}
                  </dl>
                </section>
              )}
              <section>
                <h4>Readiness</h4>
                <dl>
                  <div className="detail-row detail-row-full">
                    <dt>Control results</dt>
                    <dd>
                      <ReadinessBadges record={packageRecord} />
                    </dd>
                  </div>
                  {detailRow("Control items", state.controls.length)}
                  {detailRow("Document items", state.documents.length)}
                </dl>
              </section>
              <section>
                <h4>Scope</h4>
                <dl>
                  {detailRow("Scope assets", scopeSummary(packageRecord))}
                  {detailRow(
                    "Scope categories",
                    scopeAssetSummary(packageRecord),
                  )}
                  {detailRow(
                    "Scope exceptions",
                    scopeExceptionSummary(packageRecord),
                  )}
                </dl>
              </section>
              {hasScopeReviewContext(packageRecord) && (
                <section>
                  <h4>Scope review context</h4>
                  <dl>
                    {detailRow(
                      "Unknown-scope assets",
                      scopeReviewCounts(packageRecord).unknown,
                    )}
                    {detailRow(
                      "Likely AWS-managed/security-service",
                      scopeReviewCounts(packageRecord).awsManaged,
                    )}
                    {detailRow(
                      "Likely Control Tower baseline",
                      scopeReviewCounts(packageRecord).controlTower,
                    )}
                    {detailRow(
                      "Likely CDK bootstrap",
                      scopeReviewCounts(packageRecord).cdkBootstrap,
                    )}
                    {detailRow(
                      "Likely platform support",
                      scopeReviewCounts(packageRecord).platformSupport,
                    )}
                    {detailRow(
                      "Client-created unknown",
                      scopeReviewCounts(packageRecord).clientCreated,
                    )}
                    {detailRow(
                      "Requires client review",
                      scopeReviewCounts(packageRecord).requiresReview,
                    )}
                  </dl>
                </section>
              )}
              <section>
                <h4>Reason/action generation</h4>
                <dl>
                  <div className="detail-row">
                    <dt>Status</dt>
                    <dd>
                      <span
                        className={statusPillClassName(
                          text(packageRecord, "reasonActionStatus"),
                        )}
                      >
                        {friendlyLabel(
                          text(packageRecord, "reasonActionStatus"),
                        )}
                      </span>
                    </dd>
                  </div>
                  {detailRow("Generated", formatLocalDateTime(generatedAt))}
                  {detailRow(
                    "Controls",
                    text(packageRecord, "reasonActionControlCount"),
                  )}
                  {detailRow(
                    "Batches",
                    text(packageRecord, "reasonActionBatchCount"),
                  )}
                  {detailRow("Tokens", tokenSummary)}
                  {detailRow(
                    "Model",
                    text(packageRecord, "reasonActionModelId"),
                  )}
                  {detailRow(
                    "Prompt version",
                    text(packageRecord, "reasonActionPromptVersion"),
                  )}
                </dl>
              </section>
            </div>
          </article>

          <article className="card">
            <div className="grid-header-row">
              <div>
                <h3>Control Reason / Action Grid</h3>
                <p className="muted-text">
                  Showing {filteredControls.length} of {state.controls.length}{" "}
                  control record(s). Admin guidance is internal to SURELINC.
                </p>
              </div>
            </div>

            <div className="form-grid package-search-grid">
              <label>
                Status
                <select
                  value={controlFilter}
                  onChange={(event) =>
                    setControlFilter(event.target.value as ControlFilter)
                  }
                >
                  {CONTROL_FILTERS.map((filter) => (
                    <option key={filter.value} value={filter.value}>
                      {filter.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Category
                <select
                  value={categoryFilter}
                  onChange={(event) => setCategoryFilter(event.target.value)}
                >
                  <option value="">All categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </label>
              <label className="form-wide">
                Search
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Control ID, title, reason, action, category, owner"
                />
              </label>
            </div>

            {filteredControls.length === 0 && (
              <p>No controls matched your filters.</p>
            )}

            {filteredControls.length > 0 && (
              <div className="table-wrap admin-control-grid-wrap">
                <table className="interactive-table admin-control-grid-table">
                  <thead>
                    <tr>
                      <th>Control</th>
                      <th>Status</th>
                      <th>Admin Reason / Action</th>
                      <th>Admin Evidence Gap</th>
                      <th>Client Preview</th>
                      <th>Category / Owner</th>
                      <th>Confidence</th>
                      <th>AI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredControls.map((control, index) => (
                      <tr key={`${controlId(control) || "control"}-${index}`}>
                        <td>
                          <strong>{controlId(control)}</strong>
                          <div className="cell-secondary">
                            {text(control, "title")}
                          </div>
                        </td>
                        <td>
                          <span
                            className={statusPillClassName(
                              controlStatus(control),
                            )}
                          >
                            {controlStatus(control)}
                          </span>
                        </td>
                        <td>
                          <div className="reason-action-cell">
                            {adminReasonAction(control) || "—"}
                          </div>
                          <ControlDetail control={control} />
                        </td>
                        <td>
                          {valueOrDash(
                            text(
                              control,
                              "adminEvidenceGap",
                              "evidenceGap",
                              "clientEvidenceGap",
                            ),
                          )}
                        </td>
                        <td>
                          <span className="cell-secondary">
                            {clientReasonAction(control) || "—"}
                          </span>
                        </td>
                        <td>
                          <div>
                            {valueOrDash(text(control, "actionCategory"))}
                          </div>
                          <div className="cell-secondary">
                            {valueOrDash(text(control, "ownerHint"))}
                          </div>
                        </td>
                        <td>
                          <div>
                            {valueOrDash(
                              text(
                                control,
                                "reasonActionConfidence",
                                "confidence",
                              ),
                            )}
                          </div>
                          <div className="cell-secondary">
                            {valueOrDash(text(control, "reasonActionSource"))}
                          </div>
                        </td>
                        <td>
                          <button
                            className="secondary-button ai-table-button"
                            type="button"
                            onClick={() => askAboutControl(control)}
                          >
                            Ask AI
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <details className="raw-details">
              <summary>Raw package metadata</summary>
              <pre>{JSON.stringify(packageRecord, null, 2)}</pre>
            </details>
          </article>
        </>
      )}
    </div>
  );
}
